import {
  computeShiftAnalytics,
  type ShiftAnalyticsEvent,
  type ShiftAnalyticsResult,
} from '@/shared/lib/shiftAnalytics'
import {
  buildLocationCutoffRanges,
  unionRange,
} from '@/shared/lib/shiftPaymentCutoff'
import { buildMonthPeriodKey } from '@/shared/lib/shiftPeriodPaymentKey'
import type { LocationRecord } from '@/shared/repositories/locations/locationsRepository'
import type { ShiftLocationPeriodPaymentRecord } from '@/shared/repositories/shiftPayments/shiftLocationPeriodPaymentsRepository'
import type { ShiftTimeEntryRecord } from '@/shared/repositories/shiftTime/shiftTimeEntriesRepository'

export type PreviousMonthSnapshot = {
  year: number
  month: number
  label: string
  hours: number
  cents: number
  /** Tendência vs o mês imediatamente anterior. */
  hoursDirection: 'up' | 'down' | 'same'
  centsDirection: 'up' | 'down' | 'same'
}

export type TwelveMonthAverage = {
  avgHours: number
  avgCents: number
  monthsIncluded: number
}

export type PlantoesMonthAnalyticsOptions = {
  entries: ShiftTimeEntryRecord[]
  events: ShiftAnalyticsEvent[]
  locations: LocationRecord[]
  includedLocationIds: string[]
}

export const monthRange = (year: number, month: number) => {
  const start = new Date(Date.UTC(year, month, 1))
  const end = new Date(Date.UTC(year, month + 1, 1))
  return { start, end }
}

export const monthLabel = (year: number, month: number) =>
  new Date(Date.UTC(year, month, 15)).toLocaleDateString('pt-BR', {
    month: 'long',
    year: 'numeric',
  })

const MONTH_ABBREV_PT_BR = [
  'Jan',
  'Fev',
  'Mar',
  'Abr',
  'Mai',
  'Jun',
  'Jul',
  'Ago',
  'Set',
  'Out',
  'Nov',
  'Dez',
] as const

/** Rótulo compacto para KPIs — só mês abreviado (ex.: «Mai»), sem ano. */
export const shortMonthLabel = (_year: number, month: number) => MONTH_ABBREV_PT_BR[month]!

/** Meses anteriores exibidos nos KPIs (além do mês corrente). */
export const PRIOR_MONTHS_KPI_COUNT = 3

/** Meses de histórico para média móvel e corte de pagamento (inclui baseline de tendência). */
export const MONTH_SUMMARY_FETCH_SPAN = 12

export const offsetMonth = (
  year: number,
  month: number,
  delta: number,
): { year: number; month: number } => {
  const d = new Date(Date.UTC(year, month + delta, 1))
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() }
}

export const computeAnalyticsForCalendarMonth = (
  year: number,
  month: number,
  options: PlantoesMonthAnalyticsOptions,
): ShiftAnalyticsResult => {
  const calendarRange = monthRange(year, month)
  const ref = new Date(Date.UTC(year, month, 15))
  const locationRanges = buildLocationCutoffRanges(options.locations, ref)

  return computeShiftAnalytics({
    entries: options.entries,
    events: options.events,
    locations: options.locations,
    globalRange: calendarRange,
    locationRanges,
    includedLocationIds: options.includedLocationIds,
  })
}

export const buildFetchRangeForMonthSpan = (
  endYear: number,
  endMonth: number,
  monthCount: number,
  locations: LocationRecord[],
) => {
  const startRef = offsetMonth(endYear, endMonth, -(monthCount - 1))
  const calendarSpan = {
    start: monthRange(startRef.year, startRef.month).start,
    end: monthRange(endYear, endMonth).end,
  }

  const cutoffRanges: Array<{ start: Date; end: Date }> = []
  for (let i = 0; i < monthCount; i++) {
    const m = offsetMonth(endYear, endMonth, -i)
    const ref = new Date(Date.UTC(m.year, m.month, 15))
    cutoffRanges.push(...Object.values(buildLocationCutoffRanges(locations, ref)))
  }

  return unionRange([calendarSpan, ...cutoffRanges]) ?? calendarSpan
}

export type PlantoesMultiMonthSummary = {
  current: ShiftAnalyticsResult
  periodLabel: string
  currentShortLabel: string
  previousMonths: PreviousMonthSnapshot[]
  twelveMonthAverage: TwelveMonthAverage
  /** HOJE-04: horas dos últimos 6 meses, do mais antigo ao mês escolhido (barras no computador). */
  lastSixMonths: MonthHoursBar[]
}

export type MonthHoursBar = { year: number; month: number; label: string; hours: number }

export const LAST_MONTHS_BARS = 6

export const buildPlantoesMultiMonthSummary = (
  year: number,
  month: number,
  options: PlantoesMonthAnalyticsOptions,
): PlantoesMultiMonthSummary => {
  const current = computeAnalyticsForCalendarMonth(year, month, options)

  const priorDeltas = Array.from(
    { length: PRIOR_MONTHS_KPI_COUNT + 1 },
    (_, index) => -(index + 1),
  )
  const priorMonthAnalytics = priorDeltas.map((delta) => {
    const ref = offsetMonth(year, month, delta)
    return {
      year: ref.year,
      month: ref.month,
      analytics: computeAnalyticsForCalendarMonth(ref.year, ref.month, options),
    }
  })

  const previousMonths: PreviousMonthSnapshot[] = priorMonthAnalytics
    .slice(0, PRIOR_MONTHS_KPI_COUNT)
    .map((snap, index) => {
    const prior = priorMonthAnalytics[index + 1]!
    return {
      year: snap.year,
      month: snap.month,
      label: shortMonthLabel(snap.year, snap.month),
      hours: snap.analytics.totalHours,
      cents: snap.analytics.totalEstimatedCents,
      hoursDirection: directionOf(snap.analytics.totalHours, prior.analytics.totalHours),
      centsDirection: directionOf(
        snap.analytics.totalEstimatedCents,
        prior.analytics.totalEstimatedCents,
      ),
    }
  })

  const monthResults = Array.from({ length: 12 }, (_, i) => {
    const ref = offsetMonth(year, month, -i)
    return computeAnalyticsForCalendarMonth(ref.year, ref.month, options)
  })

  const lastSixMonths: MonthHoursBar[] = monthResults
    .slice(0, LAST_MONTHS_BARS)
    .map((row, i) => {
      const ref = offsetMonth(year, month, -i)
      return { year: ref.year, month: ref.month, label: shortMonthLabel(ref.year, ref.month), hours: row.totalHours }
    })
    .reverse()

  const totalHours = monthResults.reduce((sum, row) => sum + row.totalHours, 0)
  const totalCents = monthResults.reduce((sum, row) => sum + row.totalEstimatedCents, 0)
  const monthsIncluded = monthResults.length

  return {
    current,
    periodLabel: monthLabel(year, month),
    currentShortLabel: shortMonthLabel(year, month),
    previousMonths,
    twelveMonthAverage: {
      avgHours: totalHours / monthsIncluded,
      avgCents: totalCents / monthsIncluded,
      monthsIncluded,
    },
    lastSixMonths,
  }
}

export const directionOf = (current: number, previous: number): 'up' | 'down' | 'same' => {
  if (current > previous) return 'up'
  if (current < previous) return 'down'
  return 'same'
}

/** Máximo de linhas de breakdown visíveis nos KPIs antes do overflow «+X locais». */
export const LOCATION_BREAKDOWN_MAX_VISIBLE = 3

export type LocationMoneyBreakdownLine = {
  locationId: string
  name: string
  shiftCount: number
  hours: number
  cents: number
}

export type PlantoesMonthMoney = {
  /** Locais × mês ainda não marcados como pagos (PLT-02). */
  receivableCents: number
  receivableLocationCount: number
  /** Nomes dos locais com valor pendente (ordem alfabética pt-BR — legado da lista Hoje). */
  receivableLocationNames: string[]
  receivableShiftCount: number
  receivedCents: number
  paidShiftCount: number
  /** Breakdown por local (ordenado: valor desc → count desc → nome). Horas/contagem vêm de `byLocation`. */
  receivableBreakdown: LocationMoneyBreakdownLine[]
  paidBreakdown: LocationMoneyBreakdownLine[]
  /** Só locais que contam (PLT-03); `null` quando nenhum atendimento foi informado. */
  attendanceTotal: number | null
}

const compareLocationMoneyBreakdown = (
  a: LocationMoneyBreakdownLine,
  b: LocationMoneyBreakdownLine,
): number => {
  if (b.cents !== a.cents) return b.cents - a.cents
  if (b.shiftCount !== a.shiftCount) return b.shiftCount - a.shiftCount
  return a.name.localeCompare(b.name, 'pt-BR')
}

export const sortLocationMoneyBreakdown = (
  lines: LocationMoneyBreakdownLine[],
): LocationMoneyBreakdownLine[] => [...lines].sort(compareLocationMoneyBreakdown)

/**
 * Breakdown a receber / pago a partir das linhas de `analytics.byLocation` e do set de locais
 * pagos no período. Ignora `estimatedCents <= 0`. Horas = `row.hours` (entries fechadas via
 * `hoursBetween` em `computeShiftAnalytics`; plantão aberto não contribui).
 */
export const computeLocationMoneyBreakdown = (
  analytics: ShiftAnalyticsResult,
  paidLocationIds: ReadonlySet<string>,
): { receivable: LocationMoneyBreakdownLine[]; paid: LocationMoneyBreakdownLine[] } => {
  const receivable: LocationMoneyBreakdownLine[] = []
  const paid: LocationMoneyBreakdownLine[] = []
  for (const row of analytics.byLocation) {
    if (row.estimatedCents <= 0) continue
    const line: LocationMoneyBreakdownLine = {
      locationId: row.locationId,
      name: row.name,
      shiftCount: row.shiftCount,
      hours: row.hours,
      cents: row.estimatedCents,
    }
    if (paidLocationIds.has(row.locationId)) paid.push(line)
    else receivable.push(line)
  }
  return {
    receivable: sortLocationMoneyBreakdown(receivable),
    paid: sortLocationMoneyBreakdown(paid),
  }
}

/**
 * A receber / Recebido pelo status local × mês (`shift_location_period_payments`, chave do mês
 * como na tela de Plantões) e Atendimentos só dos locais com «Contar atendimentos».
 */
export const computePlantoesMonthMoney = (
  analytics: ShiftAnalyticsResult,
  options: {
    year: number
    month: number
    locations: LocationRecord[]
    payments: ShiftLocationPeriodPaymentRecord[]
  },
): PlantoesMonthMoney => {
  const periodKey = buildMonthPeriodKey(options.year, options.month)
  const paidLocationIds = new Set(
    options.payments
      .filter((payment) => payment.paid && payment.periodKey === periodKey)
      .map((payment) => payment.locationId),
  )
  const countsAttendances = new Set(
    options.locations.filter((location) => location.countsAttendances).map((location) => location.id),
  )
  const breakdown = computeLocationMoneyBreakdown(analytics, paidLocationIds)
  const money: PlantoesMonthMoney = {
    receivableCents: 0,
    receivableLocationCount: 0,
    receivableLocationNames: [],
    receivableShiftCount: 0,
    receivedCents: 0,
    paidShiftCount: 0,
    receivableBreakdown: breakdown.receivable,
    paidBreakdown: breakdown.paid,
    attendanceTotal: null,
  }
  for (const row of analytics.byLocation) {
    if (countsAttendances.has(row.locationId) && row.attendanceCount != null) {
      money.attendanceTotal = (money.attendanceTotal ?? 0) + row.attendanceCount
    }
    if (row.estimatedCents <= 0) continue
    if (paidLocationIds.has(row.locationId)) {
      money.receivedCents += row.estimatedCents
      money.paidShiftCount += row.shiftCount
    } else {
      money.receivableCents += row.estimatedCents
      money.receivableLocationCount += 1
      money.receivableLocationNames.push(row.name)
      money.receivableShiftCount += row.shiftCount
    }
  }
  money.receivableLocationNames.sort((a, b) => a.localeCompare(b, 'pt-BR'))
  return money
}
